import {
  createProductAnalyticsClient,
  EAnalyticsPlatform,
  type IAnalyticsProperties,
  type IProductAnalyticsClient,
  redactAnalyticsEvent,
  redactAnalyticsUrl,
  runGuardedAnalyticsCall,
} from "@frontend-shared/product-analytics";
import type { PostHog } from "posthog-js";

import { getEnvVar } from "@/env/env";

import type { ICapturePageviewProps } from "./product-analytics.types";

// NOTE: Web PostHog client (epic NSAI-2431 / NSAI-4614). A module singleton
// rather than React state, so non-component code (api clients, websocket
// handlers) can emit without threading a hook through the tree.
//
// Two properties worth not breaking:
//
//   1. `posthog-js` is imported dynamically and ONLY when a key is configured.
//      With no key the SDK never enters the bundle's critical path and nothing
//      is sent — which is how on-premise builds stay silent by default.
//   2. Every method is best-effort. An analytics failure must never surface in
//      a user-facing flow, so errors are swallowed here rather than at call
//      sites.
//
// The event contract lives in `docs/observability/posthog-tracking-plan.md`.

let postHogClient: PostHog | null = null;
let initPromise: Promise<void> | null = null;

// NOTE: `init` loads the SDK through a dynamic import, so it settles a tick or
// two after the app mounts — but the provider's pageview effect runs
// synchronously on that same mount. Without this buffer the FIRST `$pageview`
// of every session (and anything else emitted in that window) would be dropped
// while `postHogClient` is still null, undercounting every session by one and
// skewing any funnel that starts at a pageview.
//
// Calls made before the SDK is ready are held here and replayed in order once
// it is. When analytics is disabled there is no init to wait for, so nothing is
// buffered and every call stays a no-op.
let pendingCalls: Array<() => void> = [];

// NOTE: A bound, so a broken init (offline, blocked host) cannot grow this
// without limit for the lifetime of the tab. Well above what a session emits
// before the SDK settles.
const MAX_PENDING_CALLS = 50;

const runOrQueue = (call: () => void): void => {
  if (postHogClient) {
    call();
    return;
  }

  // NOTE: No init in flight means analytics is switched off, or an init already
  // failed. Either way no SDK is coming, so drop rather than queue — a buffer
  // nothing will ever flush is just a leak.
  if (!initPromise) return;

  if (pendingCalls.length < MAX_PENDING_CALLS) {
    pendingCalls.push(call);
  }
};

const flushPendingCalls = (): void => {
  const queued = pendingCalls;
  pendingCalls = [];
  queued.forEach((call) => call());
};

// NOTE: Attached to every event so one funnel can span web, mobile and the
// extension — the platform is a property, never part of the event name.
const getBaseProperties = (): IAnalyticsProperties => ({
  platform: EAnalyticsPlatform.WEB,
});

const logFailure = ({
  action,
  error,
}: {
  action: string;
  error: unknown;
}): void => {
  // NOTE: `console` rather than a toast — the user must never learn that
  // telemetry hiccuped, but a developer debugging instrumentation should.
  console.warn(
    `[analytics] ${action} failed:`,
    error instanceof Error ? error.message : error,
  );
};

export const initProductAnalytics = async (): Promise<void> => {
  const apiKey = getEnvVar("VITE_POSTHOG_KEY");

  // NOTE: No key → disabled. This single check is what covers local dev and
  // on-premise deployments; there is no second switch to forget.
  if (!apiKey) return;

  // NOTE: Awaited rather than returned, so a concurrent second caller (a
  // provider remount) joins the in-flight init instead of starting a second
  // SDK load.
  initPromise ??= (async (): Promise<void> => {
    try {
      const { default: posthog } = await import("posthog-js");
      const host = getEnvVar("VITE_POSTHOG_HOST");

      posthog.init(apiKey, {
        ...(host ? { api_host: host } : {}),
        // NOTE: NSAI-6105 — the SDK attaches location properties we never pass
        // it (`$session_entry_url` above all, which it captures once and then
        // republishes on EVERY event of the session) so sanitising our own
        // `capture` calls is not enough. This hook is the only point that sees
        // those, and it mutates in place so the SDK's own result type is
        // returned unchanged rather than asserted back into shape.
        before_send: (event) => {
          redactAnalyticsEvent({ event });
          return event;
        },
        // NOTE: Autocapture records the text content of clicked elements, which
        // would smuggle user content into properties the tracking plan forbids.
        // Every event here is declared explicitly instead.
        autocapture: false,
        capture_pageview: false,
        // NOTE: Session recording captures real user content — a direct
        // conflict with NSAI-2433. Turning it on is a separate decision.
        disable_session_recording: true,
        persistence: "localStorage+cookie",
      });

      postHogClient = posthog;
      flushPendingCalls();
    } catch (error) {
      logFailure({ action: "init", error });
      postHogClient = null;
      // NOTE: The SDK will never arrive — drop what was buffered rather than
      // holding it for the lifetime of the tab.
      pendingCalls = [];
      // NOTE: Clearing this is what stops a failed init from behaving like an
      // init that is still in flight. Left set, `runOrQueue` would keep
      // buffering calls that can never be flushed, and `??=` below would refuse
      // to retry on a later mount. Cleared, later calls become no-ops and a
      // remount gets a fresh attempt.
      initPromise = null;
    }
  })();

  await initPromise;
};

// NOTE: The scheduling, swallowing and logging live in the shared factory, so
// web and mobile cannot drift apart on the never-break-a-user-flow guarantee.
// What is web-specific is `runOrQueue`: calls made before the dynamically
// imported SDK settles are buffered rather than dropped.
export const productAnalytics: IProductAnalyticsClient =
  createProductAnalyticsClient({
    getSdk: () => postHogClient,
    schedule: runOrQueue,
    getBaseProperties,
    logFailure,
  });

// NOTE: Separate from `capture` because `$pageview` is a PostHog reserved event
// with its own `$current_url` convention, not one of our taxonomy names.
export const capturePageview = ({ path }: ICapturePageviewProps): void => {
  // NOTE: NSAI-6105 — redacted here as well as in `before_send`, deliberately.
  // The hook is what catches SDK-generated properties; doing it at the call
  // site too means a token never enters the SDK at all, so a future config
  // change that drops the hook cannot silently reopen the leak.
  const redactedPath = redactAnalyticsUrl({ url: path });

  runOrQueue(() =>
    runGuardedAnalyticsCall({
      action: "capture $pageview",
      logFailure,
      run: () => {
        postHogClient?.capture("$pageview", {
          ...getBaseProperties(),
          $current_url: `${globalThis.location.origin}${redactedPath}`,
          pathname: redactedPath,
        });
      },
    }),
  );
};

// NOTE: Test seam for asserting the no-key guarantee. Nothing consumes it yet:
// this package has no unit-test runner (Playwright only), and the mobile twin
// of this client is where that guarantee is currently covered.
export const getIsProductAnalyticsEnabled = (): boolean =>
  postHogClient !== null;

// NOTE: Exported for tests only; resets the module singleton between cases.
export const resetProductAnalyticsForTests = (): void => {
  postHogClient = null;
  initPromise = null;
  pendingCalls = [];
};
